import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, MenuItem,
  Stack, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from "@mui/material";
import { Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useHd } from "../HelpdeskContext";
import { hd, errorMessage } from "../api";
import { Section, useCrud } from "./common";
import { T, TYPE, formatDateTime, formatRelative, labelSx, primaryButtonSx } from "../../user/complaints/shared";

const th = { "& th": { ...labelSx, bgcolor: T.surfaceHeader } };

const PRESETS = {
  "Zoho Mail": { imap: { host: "imap.zoho.in", port: 993, secure: true }, smtp: { host: "smtp.zoho.in", port: 465, secure: true } },
  "Google Workspace": { imap: { host: "imap.gmail.com", port: 993, secure: true }, smtp: { host: "smtp.gmail.com", port: 465, secure: true } },
  "Microsoft 365": { imap: { host: "outlook.office365.com", port: 993, secure: true }, smtp: { host: "smtp.office365.com", port: 587, secure: false } },
};

export const MailboxesEditor = () => {
  const ctx = useHd();
  const { rows, create, update, remove, load } = useCrud("/mailboxes");
  const [edit, setEdit] = useState(null);
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);
  const [tab, setTab] = useState(0);
  if (!rows) return <CircularProgress size={22} />;

  const blank = { name: "Support", address: "", displayName: "Support", imap: { host: "", port: 993, secure: true, user: "", pass: "", folder: "INBOX" }, smtp: { host: "", port: 587, secure: false, user: "", pass: "" }, supportsPlusAddressing: false, defaultTeamId: "", defaultPriorityId: "", defaultCategoryId: "", enabled: true, startFromNow: true };
  const setS = (srv, k) => (e) => setEdit((p) => ({ ...p, [srv]: { ...p[srv], [k]: e.target.type === "checkbox" ? e.target.checked : e.target.type === "number" ? Number(e.target.value) : e.target.value } }));
  const body = () => {
    const b = { ...edit, defaultTeamId: edit.defaultTeamId || null, defaultPriorityId: edit.defaultPriorityId || null, defaultCategoryId: edit.defaultCategoryId || null };
    ["imap", "smtp"].forEach((s) => {
      b[s] = { ...b[s] };
      delete b[s].hasPassword;
      if (!b[s].pass) delete b[s].pass;
    });
    ["_id", "clientId", "createdAt", "updatedAt", "__v", "uidValidity", "lastUid", "lastPolledAt", "lastError", "lastErrorAt"].forEach((k) => delete b[k]);
    return b;
  };
  const save = async () => {
    const ok = edit._id ? await update(edit._id, body()) : await create(body());
    if (ok) setEdit(null);
  };
  const runTest = async () => {
    setTesting(true);
    setTest(null);
    try {
      setTest(await hd.testMailbox(edit._id, { imap: body().imap, smtp: body().smtp }));
    } catch (err) {
      setTest({ error: errorMessage(err) });
    } finally {
      setTesting(false);
    }
  };
  const poll = async (id) => {
    try {
      const r = await hd.pollMailbox(id);
      ctx.notify(r.result?.error ? "Poll failed — see mailbox error" : r.result?.initialised ? "Mailbox connected; new mail will be imported from now" : `Checked: ${r.result?.queued || 0} new email(s)`, r.result?.error ? "error" : "success");
      load();
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    }
  };

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}><Tab label="Mailboxes" /><Tab label="Inbound log" /></Tabs>
      {tab === 1 ? <InboundLog /> : (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Connect each support address with IMAP (to receive) and optionally SMTP (to reply from the same address). Use an <b>app password</b>. Send-only services such as ZeptoMail cannot receive email.
            Without SMTP, replies go out through the platform sender with Reply-To set to this address.
          </Alert>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
            <Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={15} />} onClick={() => { setEdit({ ...blank }); setTest(null); }}>Add mailbox</Button>
          </Stack>
          {rows.length > 0 ? (
            <Table size="small">
              <TableHead><TableRow sx={th}><TableCell>Address</TableCell><TableCell>IMAP</TableCell><TableCell>SMTP</TableCell><TableCell>Last checked</TableCell><TableCell>On</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {rows.map((m) => (
                  <TableRow key={m._id}>
                    <TableCell><Typography sx={{ fontSize: TYPE.small, fontWeight: 600 }}>{m.address}</Typography><Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{m.displayName}</Typography></TableCell>
                    <TableCell>{m.imap?.host ? `${m.imap.host}:${m.imap.port}` : <Chip size="small" label="not set" />}</TableCell>
                    <TableCell>{m.smtp?.host ? `${m.smtp.host}:${m.smtp.port}` : "platform sender"}</TableCell>
                    <TableCell>
                      {m.lastPolledAt ? formatRelative(m.lastPolledAt) : "never"}
                      {m.lastError && <Typography sx={{ fontSize: TYPE.micro, color: "#B42318" }}>{m.lastError}</Typography>}
                    </TableCell>
                    <TableCell><Switch size="small" checked={m.enabled} onChange={(e) => update(m._id, { enabled: e.target.checked })} /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <IconButton size="small" title="Check now" onClick={() => poll(m._id)}><RefreshCw size={15} /></IconButton>
                      <IconButton size="small" onClick={() => { setEdit({ ...m, imap: { ...m.imap, pass: "" }, smtp: { ...m.smtp, pass: "" }, defaultTeamId: m.defaultTeamId || "", defaultPriorityId: m.defaultPriorityId || "", defaultCategoryId: m.defaultCategoryId || "" }); setTest(null); }}><Pencil size={15} /></IconButton>
                      <IconButton size="small" onClick={() => remove(m._id)}><Trash2 size={15} color="#B42318" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", mb: 0.5, color: T.ink }}>No support mailbox connected</Typography>
              <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, mb: 2.5, maxWidth: 460, mx: "auto" }}>
                Connect a support email address to start receiving incoming customer emails as tickets automatically.
              </Typography>
              <Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={15} />} onClick={() => { setEdit({ ...blank }); setTest(null); }}>
                Add Mailbox
              </Button>
            </Box>
          )}
        </>
      )}
      {edit && (
        <Dialog open onClose={() => setEdit(null)} maxWidth="md" fullWidth>
          <DialogTitle>{edit._id ? "Edit mailbox" : "Connect mailbox"}</DialogTitle>
          <DialogContent dividers>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {Object.keys(PRESETS).map((k) => <Chip key={k} label={k} onClick={() => setEdit((p) => ({ ...p, imap: { ...p.imap, ...PRESETS[k].imap }, smtp: { ...p.smtp, ...PRESETS[k].smtp } }))} />)}
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}><TextField fullWidth label="Support address" value={edit.address} onChange={(e) => {
                const v = e.target.value;
                // keep usernames in sync with the address until the admin types their own
                setEdit((p) => {
                  const follow = (u) => (!u || u === p.address ? v : u);
                  return { ...p, address: v, imap: { ...p.imap, user: follow(p.imap.user) }, smtp: { ...p.smtp, user: follow(p.smtp.user) } };
                });
              }} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Sender name" value={edit.displayName} onChange={(e) => setEdit((p) => ({ ...p, displayName: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth label="Internal name" value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} /></Grid>
            </Grid>
            <Section title="Incoming (IMAP)">
              <Grid container spacing={2}>
                <Grid item xs={6}><TextField fullWidth size="small" label="Host" value={edit.imap.host} onChange={setS("imap", "host")} /></Grid>
                <Grid item xs={2}><TextField fullWidth size="small" type="number" label="Port" value={edit.imap.port} onChange={setS("imap", "port")} /></Grid>
                <Grid item xs={4}><FormControlLabel control={<Checkbox checked={edit.imap.secure} onChange={setS("imap", "secure")} />} label="SSL/TLS" /></Grid>
                <Grid item xs={4}><TextField fullWidth size="small" label="Username" value={edit.imap.user} onChange={setS("imap", "user")} /></Grid>
                <Grid item xs={4}><TextField fullWidth size="small" type="password" label={edit.imap.hasPassword ? "Password (saved — leave blank to keep)" : "App password"} value={edit.imap.pass} onChange={setS("imap", "pass")} autoComplete="new-password" /></Grid>
                <Grid item xs={4}><TextField fullWidth size="small" label="Folder" value={edit.imap.folder} onChange={setS("imap", "folder")} /></Grid>
              </Grid>
            </Section>
            <Section title="Outgoing (SMTP) — optional">
              <Grid container spacing={2}>
                <Grid item xs={6}><TextField fullWidth size="small" label="Host" value={edit.smtp.host} onChange={setS("smtp", "host")} /></Grid>
                <Grid item xs={2}><TextField fullWidth size="small" type="number" label="Port" value={edit.smtp.port} onChange={setS("smtp", "port")} /></Grid>
                <Grid item xs={4}><FormControlLabel control={<Checkbox checked={edit.smtp.secure} onChange={setS("smtp", "secure")} />} label="SSL (port 465)" /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" label="Username" value={edit.smtp.user} onChange={setS("smtp", "user")} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" type="password" label={edit.smtp.hasPassword ? "Password (saved — leave blank to keep)" : "App password"} value={edit.smtp.pass} onChange={setS("smtp", "pass")} autoComplete="new-password" /></Grid>
              </Grid>
            </Section>
            <Section title="Routing defaults for new tickets">
              <Grid container spacing={2}>
                <Grid item xs={4}><TextField select fullWidth size="small" label="Team" value={edit.defaultTeamId} onChange={(e) => setEdit((p) => ({ ...p, defaultTeamId: e.target.value }))}><MenuItem value="">None</MenuItem>{ctx.teams.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}</TextField></Grid>
                <Grid item xs={4}><TextField select fullWidth size="small" label="Priority" value={edit.defaultPriorityId} onChange={(e) => setEdit((p) => ({ ...p, defaultPriorityId: e.target.value }))}><MenuItem value="">Default</MenuItem>{ctx.priorities.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}</TextField></Grid>
                <Grid item xs={4}><TextField select fullWidth size="small" label="Category" value={edit.defaultCategoryId} onChange={(e) => setEdit((p) => ({ ...p, defaultCategoryId: e.target.value }))}><MenuItem value="">None</MenuItem>{ctx.categories.filter((c) => c.level === "category").map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}</TextField></Grid>
              </Grid>
              <FormControlLabel control={<Checkbox checked={edit.supportsPlusAddressing} onChange={(e) => setEdit((p) => ({ ...p, supportsPlusAddressing: e.target.checked }))} />} label="Mailbox supports plus-addressing (support+token@…) for extra-reliable threading" />
              {!edit._id && <FormControlLabel control={<Checkbox checked={edit.startFromNow} onChange={(e) => setEdit((p) => ({ ...p, startFromNow: e.target.checked }))} />} label="Only import mail that arrives after connecting" />}
            </Section>
            {test && (
              <Alert severity={test.error || test.imap?.ok === false || test.smtp?.ok === false ? "error" : "success"}>
                {test.error || <>IMAP: {test.imap ? (test.imap.ok ? `connected (${test.imap.messages} messages)` : test.imap.error) : "not set"} · SMTP: {test.smtp ? (test.smtp.ok ? "connected" : test.smtp.error) : "not set"}</>}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={runTest} disabled={testing}>{testing ? "Testing…" : "Test connection"}</Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button onClick={() => setEdit(null)}>Cancel</Button>
            <Button variant="contained" sx={primaryButtonSx} onClick={save}>Save</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

function InboundLog() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("");
  useEffect(() => {
    setRows(null);
    hd.inboundEmails(status ? { status } : {}).then(setRows).catch(() => setRows([]));
  }, [status]);
  const color = { done: "success", ignored: "default", spam: "warning", failed: "error", queued: "info", processing: "info" };
  return (
    <Box>
      <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ mb: 1.5, minWidth: 160 }}>
        <MenuItem value="">All</MenuItem>{Object.keys(color).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
      </TextField>
      {!rows ? <CircularProgress size={20} /> : (
        <Table size="small">
          <TableHead><TableRow sx={th}><TableCell>Received</TableCell><TableCell>From</TableCell><TableCell>Subject</TableCell><TableCell>Result</TableCell></TableRow></TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r._id}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(r.createdAt)}</TableCell>
                <TableCell>{r.from}</TableCell>
                <TableCell>{r.subject}</TableCell>
                <TableCell><Chip size="small" color={color[r.status]} label={r.status} />{(r.reason || r.error) && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{r.reason || r.error}</Typography>}</TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={4} sx={{ color: T.inkMuted }}>No inbound email yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

const EVENT_LABEL = {
  "ticket.created": "Ticket created (acknowledgement)", "ticket.assigned": "Ticket assigned", "ticket.updated": "Ticket updated", "agent.replied": "Agent reply",
  "customer.replied": "Customer replied", "ticket.resolved": "Ticket resolved", "ticket.closed": "Ticket closed", "sla.warning": "SLA warning", "sla.breached": "SLA breach",
  "agent.assignment": "Agent assignment", "chat.handoff": "Chat handoff", "csat.request": "CSAT survey",
};
const SAMPLE = { customer_name: "Riya Sharma", customer_email: "riya@example.com", ticket_id: "JW-10291", ticket_subject: "Unable to login", ticket_status: "Resolved", ticket_priority: "High", agent_name: "Aman", team_name: "Technical", category: "Account & Login", resolution: "Password reset link re-sent and verified.", created_at: "26 Sep 2026, 10:12", updated_at: "26 Sep 2026, 12:40", ticket_url: "https://app.justtawk.com/helpdesk/tickets/…", csat_url: "https://app.justtawk.com/csat/…", message: "Thanks for waiting — I have reset your access.", company_name: "JustWalk" };

export const TemplatesEditor = () => {
  const { rows, create, update } = useCrud("/templates");
  const [vars, setVars] = useState([]);
  const [edit, setEdit] = useState(null);
  useEffect(() => {
    hd.templateVariables().then(setVars).catch(() => {});
  }, []);
  const preview = useMemo(() => {
    if (!edit) return null;
    const fill = (s) => String(s || "").replace(/{{\s*([a-z_]+)\s*}}/g, (all, k) => (k in SAMPLE ? SAMPLE[k] : `⚠ ${all}`));
    return { subject: fill(edit.subject), body: fill(edit.body) };
  }, [edit]);
  if (!rows) return <CircularProgress size={22} />;
  const unknown = edit ? [...`${edit.subject} ${edit.body}`.matchAll(/{{\s*([a-zA-Z0-9_]+)\s*}}/g)].map((m) => m[1]).filter((v) => !vars.includes(v)) : [];
  const save = async () => {
    const body = { event: edit.event, audience: edit.audience, channel: "email", name: edit.name, subject: edit.subject, body: edit.body, enabled: edit.enabled };
    const ok = edit._id ? await update(edit._id, body) : await create(body);
    if (ok) setEdit(null);
  };
  const byAudience = (a) => rows.filter((r) => r.audience === a);
  return (
    <Box>
      {["customer", "agent"].map((aud) => (
        <Section key={aud} title={aud === "customer" ? "Emails to customers" : "Emails to agents"}>
          <Table size="small">
            <TableBody>
              {byAudience(aud).map((t) => (
                <TableRow key={t._id} hover sx={{ cursor: "pointer" }} onClick={() => setEdit({ ...t })}>
                  <TableCell sx={{ fontWeight: 600, width: 260 }}>{EVENT_LABEL[t.event] || t.event}</TableCell>
                  <TableCell sx={{ color: T.inkSecondary }}>{t.subject}</TableCell>
                  <TableCell align="right"><Switch size="small" checked={t.enabled} onClick={(e) => e.stopPropagation()} onChange={(e) => update(t._id, { enabled: e.target.checked })} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      ))}
      <Button startIcon={<Plus size={15} />} onClick={() => setEdit({ event: "ticket.updated", audience: "customer", name: "Ticket updated", subject: "Update on {{ticket_id}}", body: "Hi {{customer_name}},\n\n", enabled: true })}>Add template</Button>
      {edit && (
        <Dialog open onClose={() => setEdit(null)} maxWidth="lg" fullWidth>
          <DialogTitle>{EVENT_LABEL[edit.event] || "Template"}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                {!edit._id && (
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <TextField select size="small" label="Event" value={edit.event} onChange={(e) => setEdit((p) => ({ ...p, event: e.target.value }))} sx={{ minWidth: 240 }}>{Object.entries(EVENT_LABEL).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}</TextField>
                    <TextField select size="small" label="Audience" value={edit.audience} onChange={(e) => setEdit((p) => ({ ...p, audience: e.target.value }))}><MenuItem value="customer">Customer</MenuItem><MenuItem value="agent">Agent</MenuItem></TextField>
                    <TextField size="small" label="Name" value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} />
                  </Stack>
                )}
                <TextField fullWidth label="Subject" value={edit.subject} onChange={(e) => setEdit((p) => ({ ...p, subject: e.target.value }))} sx={{ mb: 2 }} />
                <TextField fullWidth multiline minRows={12} label="Body" value={edit.body} onChange={(e) => setEdit((p) => ({ ...p, body: e.target.value }))} />
                <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mt: 1, mb: 0.5 }}>Click to insert:</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {vars.map((v) => <Chip key={v} size="small" label={`{{${v}}}`} onClick={() => setEdit((p) => ({ ...p, body: `${p.body}{{${v}}}` }))} sx={{ fontFamily: "monospace" }} />)}
                </Stack>
                {unknown.length > 0 && <Alert severity="error" sx={{ mt: 1.5 }}>Unknown variables: {unknown.map((u) => `{{${u}}}`).join(", ")}</Alert>}
                <FormControlLabel sx={{ mt: 1 }} control={<Checkbox checked={edit.enabled} onChange={(e) => setEdit((p) => ({ ...p, enabled: e.target.checked }))} />} label="Enabled" />
              </Grid>
              <Grid item xs={12} md={5}>
                <Typography sx={{ ...labelSx, mb: 1 }}>Preview (sample data)</Typography>
                <Box sx={{ border: `1px solid ${T.border}`, borderRadius: "10px", p: 2, bgcolor: "#FCFCFD" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: TYPE.small, mb: 1.5 }}>{preview.subject}</Typography>
                  <Typography sx={{ fontSize: TYPE.small, whiteSpace: "pre-wrap" }}>{preview.body}</Typography>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions><Button onClick={() => setEdit(null)}>Cancel</Button><Button variant="contained" sx={primaryButtonSx} onClick={save} disabled={unknown.length > 0}>Save</Button></DialogActions>
        </Dialog>
      )}
    </Box>
  );
};
